import {ButtonType} from "../modules/message-renderer-call-to-action/src/models/CallToActionResponseActions";
import {ErrorLink} from "../modules/message-renderer-errors/src/models/ErrorLinks";

export interface AlmeSettings {
    ClientSessionInactivityMs: number;
    ClientSessionTimeout: number;
    ClientSessionTimeoutWarning: number;
}

export interface AgentAssistAlmeSettings extends AlmeSettings {
    AgentAssistWebSocketUrl: string;
    AgentAssistInfoLogging: boolean;
}

export interface CallToActionButtonTypeSettings
{
    PreAlme?: unknown;
}

export interface CallToActionButtonSettings
{
    CallToActionButton?: {
        [key in ButtonType]?: CallToActionButtonTypeSettings;
    };
}

export type ValidOperator = 'equal' | 'eq' | 'not-equal' | 'notEqual' | 'ne' | 'contains';

export interface PathCondition<T> {
    /**
     * Dot-separated path to check for condition
     */
    path?: string;
    statePath?: string;
    /**
     * Short-hand operator indicating a condition (returning a bool)
     */
    operator: ValidOperator;
    /**
     * A value to use with the operator to check an object path
     */
    value: T;
}

export default {
    Alme: {
        ClientSessionInactivityMs: 5000,
        ClientSessionTimeout: 20,
        ClientSessionTimeoutWarning: 2,
    } as AlmeSettings,
    AppEvent: {
        endFeedback: {
            eventName: "EndFeedback",
            parameters: {
                formId: "formId",
                feedback: "feedback",
                endType: "endType",
            },
        },
    },
    AppEvents: {
        userLogin: {
            eventName: "OnUserLogIn",
        },
        userLogout: {
            eventName: "OnUserLogOut",
        },
        endFeedback: {
            eventName: "FormResults",
            parameters: {
                formId: "formId",
                feedback: "feedback",
                endType: "endType",
            },
        },
    },
    CalendarWidget: {
        incomingDateFormat: "YYYYMMDD", //Moment formats: https://momentjs.com/docs/#/displaying/
        incomingTimeFormat: "HH:mm",
        outgoingDateFormat: "YYYY-MM-DD",
        outgoingTimeFormat: "HH:mm",
    },
    CallToActionButton: {} as CallToActionButtonSettings,
    cookieCommunicator: {
        cookiePolInterval: 1000,
        options: {
            // See https://www.npmjs.com/package/browser-cookies#options
            set: {

            },
            erase: {

            },
        },
    },
    Cookies: {
        NITAlme: {
            getOptions: {

            },
            setOptions: {

            },
        },
        NITAlmeUser: {
            getOptions: {

            },
            setOptions: {
                expires: 360,
            },
        },
        domainMap: {},
    },
    deferredAppcallConstants: {
        versionReporterAppCallName: "GetAllVersions",
    },
    DNN: {
        urls: [
            [
                {
                    path: "hostname",
                    operator: "equal",
                    value: "localhost",
                },
                {
                    path: "search",
                    operator: "contains",
                    value: "DNN",
                },
            ],
            [
                {
                    path: "hostname",
                    operator: "equal",
                    value: "hm-dev01.ebu.nextnet.com",
                },
                {
                    path: "search",
                    operator: "contains",
                    value: "DNN",
                },
            ],
            [
                {
                    path: "hostname",
                    operator: "equal",
                    value: "10.20.10.66",
                },
                {
                    path: "search",
                    operator: "contains",
                    value: "DNN",
                },
            ],
            [
                {
                    path: "hostname",
                    operator: "equal",
                    value: "hm-mock01.ebu.nextnet.com",
                },
                {
                    path: "search",
                    operator: "contains",
                    value: "DNN",
                },
            ],
        ],
    },
    Drag: {
        iframeFix: true,
        opacity: 0.5,
        scroll: false,
        snap: false,
        snapGutterPixels: 25,
        snapMode: 'inner',
        snapTolerance: 30,
    },
    ElementDisabler: {
        callToActionDisableWhen: [],
        displayLinkDisableWhen: [],
        displayLinkFoldDisableWhen: [],
        inlineSurveyButtonDisableWhen: [],
        inputBoxDisableWhen: [],
        inputSubmitButtonDisableWhen: [],
        minimizeButtonDisableWhen: [],
        navigationLinksDisableWhen: [],
        responseMessageDisableWhen: [],
        skipToContentButtonDisableWhen: [],
    },
    endChatWarning: {
        ignoreBundledDisabler: false,
        isWarningEnabled: true,
    },
    endSession: {
        ignoreEventName: [],
        ignoreMessageSource: [],
        notificationMessage: "Your session has ended",
    },
    Error: {
        displayMultipleErrorsInHistory: true,
        defaultLinks: [] as ErrorLink[],
        launchLinks: [] as ErrorLink[],

    },
    Feedback: {
        endTypes: {
            aborted: "Aborted",
            completed: "Completed",
        },
        formId: "1", //Replace with env var for ABtesting
    },
    FPMLParameters: {
    },
    History: {
        includeRevertedEntries: false,
        messageLimit: 0,
        appendDelay: 0,
    },
    InlineSurveyLinks: {
        SurveyIdPrefix: "nit-survey-",
    },
    inputBox: {
        keepPlaceholder: false,
        maxLength: 200,
    },
    InputBoxLock: {
        suppressWhen: [] as PathCondition<unknown>[],
    },
    inputConstants: {
        parameterNames: {
            preTranslatedInput: "",
        },
    },
    itemAccordion: {
        buildMainLink: false,
        itemPanelLimit: 5,
    },
    IvaAvailability: {
        dstEndDate: "",
        dstStartDate: "",
        enableTimeBasedAvailability: false,
        endHour: "",
        endMinute: "",
        endOperatingDay: "",
        startHour: "",
        startMinute: "",
        startOperatingDay: "",
    },
    launchPoint: {
        globalFunctionName: "NITLaunchAgent",
        globalObjectName: "NITGlobal",
        setupGlobal: true,
    },
    languageSpecificSettingOverrides: {
        "en-US": {
            "textConstants.viewPageLinkText": "View Page",
            "textConstants.moreLinksText": "More >>",
            "textConstants.displayLinksHeaderText": "More Information",
        },
    },
    logging: {
        enabled: process.env.console_logging_enabled,
        ignoreActionTypes: [
            "PERSIST_STATE_CHANGE",
            "RESTORE_PERSISTED_STATE",
            "SET_INACTIVITY_TIME",
            "SET_WINDOW_POSITION",
        ],
    },
    Mobile: {
        breakpoint: 768,
    },
    newSessionGenerator: {
        stateSets: "",
    },
    notificationsApi: {
        extensibleMessageSource: 'GetNotifications',
        pollIntervalMS: 1000,
        pollStartActions: [],
        pollStopActions: [],
        throttleIntervalMS: 20000,
        throttleMessageOrigins: [],
    },
    PageAwareness: {
        type: "embedded",
    },
    Popper: {
        responseOffset: 60,
    },
    Resize: {
        alsoResize: ".also-resize",
        autoFitToViewPort: false,
        border: 15,
        containment: "document",
        handlers: "all",
        iframeFix: true,
        minHeight: 400,
        minWidth: 350,
        maxHeight: 10000,
        maxWidth: 10000,
    },
    Scroll: {
        almeResponseQuery: null,
        conversationAreaQuery: null,
        userInputQuery: null,
    },
    Scrolling: {
        ScrollInterval: 500,
    },
    SmoothScroll: {
        On: false,
    },
    session: {
        ignoreMessageSource: [],
        ignoreMessageType: [],
    },
    SessionIdToInParams: {
        inParamName: "sessionId",
    },
    SessionStorage: {
        storageKey: "NITAlme",
    },
    skinning: {
        dataAttributeName: "skinning",
        dataAttributes: {
            default: "default",
        },
        defaultSkin: "default",
        rootSelector: "#nit-alme-window-root",
        changeRootClass: false,
    },
    stateCopy: {
        copyLocations: {
        },
    },
    StateLocationsToInparams: {
        mappings: {},
    },
    stepOutIcon: {
        ivaSubdomains: ['*'],
        ivaDomains: ['localhost'],
        iconSvg: '<sup><svg class="step-out-header-link" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" focusable="false" height="12px" width="12px">' +
            '<title>This indicates a link to an external site that may not follow the same accessibility policies.</title><g>' +
            '<path d="m397 512l-360 0c-21 0-37-17-37-37l0-361c0-20 16-37 37-37l119 0c20 0 36 17 36 37 0 20-16 37-36 37l-82 0 0 287 287 0 0-82c0-20 16-36 36-36 21 0 37 16 37 36l0 119c0 20-16 37-37 37z m78-259c-20 0-37-16-37-36l0-143-142 0c-20 0-36-17-36-37 0-20 16-37 36-37l179 0c20 0 37 17 37 37l0 180c0 20-16 36-37 36z m-178 5c-9 0-18-4-25-11-15-14-15-37-1-51l177-184c14-14 37-15 52-1 15 15 15 38 1 52l-177 184c-8 7-17 11-27 11z">' +
            '</path></g></svg></sup>',
    },
    time: {
        offsetParamName: "ClientTimeZoneOffset",
        timeZoneNameParamName: "ClientTimeZoneName",
    },
    TimedAppEvent: {
        maxTimerThresholdInSeconds: 600,
    },
    timeoutWarning: {
        ignoreBundledDisabler: false,
        reportingEventName: "UserRefreshedSessionTimeout",
        isDisabled: false,
        minimizeOnTimeout: false,
    },
    TTS: {
        alwaysDisplayAudioControls: false,
        chatTTSEnabled: true,
        clearSoundAfterDisabling: true,
        clearSoundAfterStart: false,
        onlyUseFlashAudio: false,
        stopSoundAfterMinimize: true,
        TTS_BASE_PATH: "/AlmeAPI/",
        ttsAudioId: "alme-audio",
        ttsCloseSoundId: "alme-close-sound-control",
        ttsDivId: "alme-tts",
        ttsMediaId: "alme-movie",
    },
    uiVersionNumberParam: "UiVersion",
    userMetaDataProcessorSettings: {
        AppEventFilteringEnabled: "false",
        AppEventsToApply: [],
    },
    version: "0.0.1",
    WeblinkAwareness: {
        inputParameterName: "WeblinkAwareness",
    },
    weblinkConstants: {},
    welcomeUser: {
        suppressWithLaunchQuestion: true,
        allowActivePonClicks: false,
    },
    ignoreTimeoutResponseActions:{
        responseActionValues: ["DisplayForm"],
    },
    launchQuestionReportingEvent: {
        enabled: false,
    },
    enableLessFeature: {
        enabled: false,
    },
    processPostMessageAccessKey: {
        enabled: true,
    },
    enableFeedbackModalSubmitBeforeCancelButtonOrder: {
        enabled: true,
    },
    footerCounterSettings:{
     colorChange: false,
     hidden:false,
     counterArr: [
        {
            value: "",
            color: "",
            maxValue: false,
        }
    ]
    },
    predictiveSearchSuggestions: {
        enabled: false,
        maxSuggestion: 0, 
    },
    EnableInputBoxForTheseAccessKeys:['5fc030d4-a143-4db5-b60b-7701b4f7d001'],
    formElementSettings:{
        elementArr: [
            {
                elementId: "",
                regex: /pattern/,
            }
        ]
    },
    escapeOpenCloseTags: false,
    disableIVAStateChange: false
};



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/settings/settings.ts